import { useEffect, useRef } from "react";
import { Bold, Italic, List, ListOrdered, Underline } from "lucide-react";
import { useLingui } from "@lingui/react/macro";
import { Button } from "@/components/ui/button";
import { richTextHtml, sanitizeRichText } from "@/lib/rich-text";

interface RichTextEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  minHeight?: string;
  readOnly?: boolean;
}

export function RichTextEditor({ value, onChange, placeholder, minHeight = "7.5rem", readOnly = false }: RichTextEditorProps) {
  const { t } = useLingui();
  const editorRef = useRef<HTMLDivElement>(null);
  const isFocused = useRef(false);

  useEffect(() => {
    if (editorRef.current && !isFocused.current && editorRef.current.innerHTML !== richTextHtml(value)) {
      editorRef.current.innerHTML = richTextHtml(value);
    }
  }, [value]);

  function runCommand(command: "bold" | "italic" | "underline" | "insertUnorderedList" | "insertOrderedList") {
    editorRef.current?.focus();
    document.execCommand(command);
    if (editorRef.current) onChange(sanitizeRichText(editorRef.current.innerHTML));
  }

  return (
    <div className="overflow-hidden rounded-lg border border-(--color-border) bg-(--color-surface-content)">
      <div className="flex items-center gap-1 border-b border-(--color-border) bg-(--color-surface-muted) p-1">
        <ToolbarButton label={t`Bold`} disabled={readOnly} onClick={() => runCommand("bold")}><Bold className="size-4" /></ToolbarButton>
        <ToolbarButton label={t`Italic`} disabled={readOnly} onClick={() => runCommand("italic")}><Italic className="size-4" /></ToolbarButton>
        <ToolbarButton label={t`Underline`} disabled={readOnly} onClick={() => runCommand("underline")}><Underline className="size-4" /></ToolbarButton>
        <span className="mx-1 h-5 w-px bg-(--color-border)" />
        <ToolbarButton label={t`Bulleted list`} disabled={readOnly} onClick={() => runCommand("insertUnorderedList")}><List className="size-4" /></ToolbarButton>
        <ToolbarButton label={t`Numbered list`} disabled={readOnly} onClick={() => runCommand("insertOrderedList")}><ListOrdered className="size-4" /></ToolbarButton>
      </div>
      <div
        ref={editorRef}
        contentEditable={!readOnly}
        role="textbox"
        aria-multiline="true"
        aria-readonly={readOnly || undefined}
        aria-label={t`Rich text description`}
        data-placeholder={placeholder ?? t`Add detailed instructions...`}
        onFocus={() => { isFocused.current = true; }}
        onBlur={() => { isFocused.current = false; }}
        onInput={(event) => onChange(sanitizeRichText(event.currentTarget.innerHTML))}
        className="min-h-(--editor-min-height) px-3 py-2.5 text-sm text-(--color-text-primary) outline-none [&_ol]:list-decimal [&_ol]:pl-5 [&_ul]:list-disc [&_ul]:pl-5 empty:before:pointer-events-none empty:before:text-(--color-text-secondary) empty:before:content-[attr(data-placeholder)]"
        style={{ "--editor-min-height": minHeight } as React.CSSProperties}
        suppressContentEditableWarning
      />
    </div>
  );
}

function ToolbarButton({ label, onClick, disabled, children }: { label: string; onClick: () => void; disabled?: boolean; children: React.ReactNode }) {
  return (
    <Button type="button" variant="ghost" size="icon" aria-label={label} title={label} disabled={disabled} onMouseDown={(event) => event.preventDefault()} onClick={onClick} className="size-8">
      {children}
    </Button>
  );
}